import type { Prisma, PrismaClient } from "@calcom/prisma/client";
import { SchedulingType } from "@calcom/prisma/enums";

export const SELFIE_ROOM_PROMO_METADATA_KEY = "selfieroomPromoCode";
export const SELFIE_ROOM_STRIPE_MINIMUM_CENTS = 50;

export type SelfieRoomPromoCode = {
  id: string;
  code: string;
  active: boolean;
  discountType: "percent" | "fixed";
  discountAmount: number;
  expiresAt?: string | null;
  maxRedemptions?: number | null;
  eventTypeIds?: number[];
  createdAt: string;
};

export type AppliedSelfieRoomPromoCode = {
  code: string;
  discountType: SelfieRoomPromoCode["discountType"];
  discountAmount: number;
  discountValue: number;
  originalAmount: number;
  discountedAmount: number;
  currency: string;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export const normalizeSelfieRoomPromoCode = (value: string) =>
  value
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9_-]/g, "");

const isPromoCode = (value: unknown): value is SelfieRoomPromoCode => {
  if (!isRecord(value)) return false;
  return (
    typeof value.id === "string" &&
    typeof value.code === "string" &&
    typeof value.active === "boolean" &&
    (value.discountType === "percent" || value.discountType === "fixed") &&
    typeof value.discountAmount === "number" &&
    Number.isFinite(value.discountAmount)
  );
};

type SelfieRoomPromoCodeRecord = {
  id: string;
  code: string;
  active: boolean;
  discountType: string;
  discountAmount: number;
  expiresAt: Date | null;
  maxRedemptions: number | null;
  eventTypeIds: number[];
  createdAt: Date;
};

const getPromoCodeDelegate = (prisma: PrismaClient) =>
  (
    prisma as unknown as {
      selfieRoomPromoCode?: {
        findMany: (args?: unknown) => Promise<SelfieRoomPromoCodeRecord[]>;
        findUnique: (args: unknown) => Promise<SelfieRoomPromoCodeRecord | null>;
        create: (args: unknown) => Promise<SelfieRoomPromoCodeRecord>;
        update: (args: unknown) => Promise<SelfieRoomPromoCodeRecord>;
        delete: (args: unknown) => Promise<SelfieRoomPromoCodeRecord>;
      };
    }
  ).selfieRoomPromoCode;

const promoCodeFromRecord = (record: SelfieRoomPromoCodeRecord): SelfieRoomPromoCode => ({
  id: record.id,
  code: record.code,
  active: record.active,
  discountType: record.discountType === "fixed" ? "fixed" : "percent",
  discountAmount: record.discountAmount,
  expiresAt: record.expiresAt?.toISOString() ?? null,
  maxRedemptions: record.maxRedemptions,
  eventTypeIds: record.eventTypeIds,
  createdAt: record.createdAt.toISOString(),
});

export const getSelfieRoomPromoCodesFromMetadata = (metadata: Prisma.JsonValue | null | undefined) => {
  if (!isRecord(metadata)) return [];
  const selfieroom = metadata.selfieroom;
  if (!isRecord(selfieroom) || !Array.isArray(selfieroom.promoCodes)) return [];
  return selfieroom.promoCodes.filter(isPromoCode);
};

export const setSelfieRoomPromoCodesOnMetadata = (
  metadata: Prisma.JsonValue | null | undefined,
  promoCodes: SelfieRoomPromoCode[]
) => {
  const root = isRecord(metadata) ? { ...metadata } : {};
  const selfieroom = isRecord(root.selfieroom) ? { ...root.selfieroom } : {};

  return {
    ...root,
    selfieroom: {
      ...selfieroom,
      promoCodes,
    },
  } as unknown as Prisma.InputJsonValue;
};

export const formatSelfieRoomPromoDiscount = (promoCode: SelfieRoomPromoCode) => {
  if (promoCode.discountType === "percent") return `${promoCode.discountAmount}%`;
  return `$${(promoCode.discountAmount / 100).toFixed(2)} CAD`;
};

export const getSelfieRoomPromoCodeUsageCount = async ({
  prisma,
  code,
}: {
  prisma: PrismaClient;
  code: string;
}) =>
  prisma.payment.count({
    where: {
      refunded: false,
      data: {
        path: ["selfieroomPromo", "code"],
        equals: code,
      },
    },
  });

export const getSelfieRoomPromoCodes = async ({ prisma }: { prisma: PrismaClient }) => {
  const promoCodeDelegate = getPromoCodeDelegate(prisma);
  const databasePromoCodes = promoCodeDelegate
    ? (await promoCodeDelegate.findMany({ orderBy: [{ createdAt: "desc" }] })).map(promoCodeFromRecord)
    : [];

  const selfieroomUser = await prisma.user.findFirst({
    where: { username: "selfieroom" },
    select: { metadata: true },
  });

  const metadataPromoCodes = getSelfieRoomPromoCodesFromMetadata(selfieroomUser?.metadata);
  const databaseCodes = new Set(databasePromoCodes.map((promoCode) => promoCode.code));

  return [
    ...databasePromoCodes,
    ...metadataPromoCodes.filter((promoCode) => !databaseCodes.has(promoCode.code)),
  ];
};

export const createSelfieRoomPromoCode = async ({
  prisma,
  promoCode,
}: {
  prisma: PrismaClient;
  promoCode: SelfieRoomPromoCode;
}) => {
  const promoCodeDelegate = getPromoCodeDelegate(prisma);

  if (!promoCodeDelegate) {
    throw new Error("Selfie Room promo code storage is not ready. Run database migrations first.");
  }

  return promoCodeFromRecord(
    await promoCodeDelegate.create({
      data: {
        code: promoCode.code,
        active: promoCode.active,
        discountType: promoCode.discountType,
        discountAmount: promoCode.discountAmount,
        expiresAt: promoCode.expiresAt ? new Date(promoCode.expiresAt) : null,
        maxRedemptions: promoCode.maxRedemptions,
        eventTypeIds: promoCode.eventTypeIds ?? [],
        createdAt: new Date(promoCode.createdAt),
      },
    })
  );
};

export const updateSelfieRoomPromoCodeById = async ({
  prisma,
  promoCodeId,
  active,
}: {
  prisma: PrismaClient;
  promoCodeId: string;
  active: boolean;
}) => {
  const promoCodeDelegate = getPromoCodeDelegate(prisma);
  if (!promoCodeDelegate) return null;

  const promoCode = await promoCodeDelegate.findUnique({ where: { id: promoCodeId } });
  if (!promoCode) return null;

  return promoCodeFromRecord(
    await promoCodeDelegate.update({
      where: { id: promoCodeId },
      data: { active },
    })
  );
};

export const deleteSelfieRoomPromoCodeById = async ({
  prisma,
  promoCodeId,
}: {
  prisma: PrismaClient;
  promoCodeId: string;
}) => {
  const promoCodeDelegate = getPromoCodeDelegate(prisma);
  if (!promoCodeDelegate) return null;

  const promoCode = await promoCodeDelegate.findUnique({ where: { id: promoCodeId } });
  if (!promoCode) return null;

  await promoCodeDelegate.delete({ where: { id: promoCodeId } });
  return promoCodeFromRecord(promoCode);
};

export const isSelfieRoomServiceEventType = async ({
  prisma,
  eventTypeId,
}: {
  prisma: PrismaClient;
  eventTypeId: number | null | undefined;
}) => {
  if (!eventTypeId) return false;

  const selfieroomService = await prisma.eventType.findFirst({
    where: {
      id: eventTypeId,
      OR: [
        {
          metadata: {
            path: ["selfieroom", "service"],
            equals: true,
          },
        },
        {
          title: {
            startsWith: "Selfie Room",
            mode: "insensitive",
          },
        },
        {
          slug: {
            contains: "selfie-room",
            mode: "insensitive",
          },
        },
        {
          schedulingType: SchedulingType.ROUND_ROBIN,
        },
      ],
    },
    select: { id: true, metadata: true },
  });

  return Boolean(selfieroomService);
};

export const resolveSelfieRoomPromoCode = async ({
  prisma,
  rawCode,
  eventTypeId,
  amount,
  currency,
}: {
  prisma: PrismaClient;
  rawCode: string | undefined;
  eventTypeId: number | null | undefined;
  amount: number;
  currency: string;
}): Promise<AppliedSelfieRoomPromoCode | null> => {
  const code = normalizeSelfieRoomPromoCode(rawCode ?? "");
  if (!code) return null;

  const promoCodes = await getSelfieRoomPromoCodes({ prisma });
  const promoCode = promoCodes.find((candidate) => candidate.code === code);

  if (!promoCode) {
    throw new Error("Promo code is not valid.");
  }

  if (!promoCode.active) {
    throw new Error("Promo code is not active.");
  }

  if (promoCode.expiresAt && new Date(promoCode.expiresAt).getTime() < Date.now()) {
    throw new Error("Promo code has expired.");
  }

  if (!(await isSelfieRoomServiceEventType({ prisma, eventTypeId }))) {
    throw new Error("Promo code does not apply to this service.");
  }

  if (promoCode.eventTypeIds?.length && (!eventTypeId || !promoCode.eventTypeIds.includes(eventTypeId))) {
    throw new Error("Promo code does not apply to this service.");
  }

  if (promoCode.maxRedemptions) {
    const usageCount = await getSelfieRoomPromoCodeUsageCount({ prisma, code });
    if (usageCount >= promoCode.maxRedemptions) {
      throw new Error("Promo code has reached its redemption limit.");
    }
  }

  const discountValue =
    promoCode.discountType === "percent"
      ? Math.floor((amount * promoCode.discountAmount) / 100)
      : Math.min(promoCode.discountAmount, amount);
  const discountedAmount = amount - discountValue;

  if (discountValue <= 0) {
    throw new Error("Promo code does not change this booking price.");
  }

  if (discountedAmount < SELFIE_ROOM_STRIPE_MINIMUM_CENTS) {
    throw new Error("Promo code would bring the total below Stripe's minimum charge.");
  }

  return {
    code,
    discountType: promoCode.discountType,
    discountAmount: promoCode.discountAmount,
    discountValue,
    originalAmount: amount,
    discountedAmount,
    currency,
  };
};
